import React, { useState, useEffect } from "react";
import { useGetIdentity } from "@refinedev/core";
import { User } from "../../interfaces/user";

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const UserProfilePage: React.FC = () => {
  const { data: user } = useGetIdentity<User>();
  const [userInfo, setUserInfo] = useState(user);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState<ChangePasswordData>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

  // 格式化角色显示
  const formatRole = (role: string) => {
    const roleMap: Record<string, string> = {
      admin: "管理員",
      maintainer: "維護人員",
      user: "一般用戶",
    };
    return roleMap[role] || role;
  };

  // 格式化状态显示
  const formatStatus = (isActive: boolean) => (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
      }`}
    >
      {isActive ? "啟用" : "停用"}
    </span>
  );

  // 格式化日期显示
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("zh-TW") +
      " " +
      date.toLocaleTimeString("zh-TW", {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  // 验证密码
  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) {
      errors.push("密碼長度至少8個字元");
    }
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push("密碼必須包含至少一個小寫字母");
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push("密碼必須包含至少一個大寫字母");
    }
    if (!/(?=.*\d)/.test(password)) {
      errors.push("密碼必須包含至少一個數字");
    }
    return errors;
  };

  // 处理密码表单变更
  const handlePasswordChange = (field: keyof ChangePasswordData, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));

    // 清除相关错误
    setPasswordErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];

      // 如果是新密码字段，验证密码强度
      if (field === 'newPassword' && value) {
        const passwordValidationErrors = validatePassword(value);
        if (passwordValidationErrors.length > 0) {
          newErrors[field] = passwordValidationErrors.join(', ');
        }
      }

      // 如果是确认密码字段，检查是否与新密码匹配
      if (field === 'confirmPassword' && value !== passwordData.newPassword) {
        newErrors[field] = '確認密碼與新密碼不符';
      }

      return newErrors;
    });
  };

  // 提交密码修改
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // 验证表单
    const errors: Record<string, string> = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = '請輸入當前密碼';
    }

    if (!passwordData.newPassword) {
      errors.newPassword = '請輸入新密碼';
    } else {
      const passwordValidationErrors = validatePassword(passwordData.newPassword);
      if (passwordValidationErrors.length > 0) {
        errors.newPassword = passwordValidationErrors.join(', ');
      }
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = '請輸入確認密碼';
    } else if (passwordData.confirmPassword !== passwordData.newPassword) {
      errors.confirmPassword = '確認密碼與新密碼不符';
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("未找到授權令牌");
      }

      const API_URL = import.meta.env.VITE_API_URL || "http://imdf.sto-tek.com/";
      const response = await fetch(`${API_URL}/auth/change-password`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setSuccess("密碼修改成功！即將重新導向到登入頁面...");
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setPasswordErrors({});
        setShowChangePassword(false);

        // 3秒後跳轉到登入頁面（因為密碼修改會登出所有設備）
        setTimeout(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("refreshToken");
          window.location.href = "/login";
        }, 3000);
      } else {
        throw new Error(data.message || "修改密碼失敗");
      }
    } catch (err) {
      console.error("修改密碼錯誤:", err);
      setError(err instanceof Error ? err.message : "修改密碼失敗");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* 页面标题 */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">個人資料</h1>
          <p className="mt-2 text-sm text-gray-700">
            查看和管理您的個人帳號資訊。
          </p>
        </div>
      </div>

      {/* 成功和错误消息 */}
      {success && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* 用户信息卡片 */}
      <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-20 w-20">
              <div className="h-20 w-20 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-2xl font-medium text-blue-600">
                  {userInfo?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
            </div>
            <div className="ml-6">
              <h2 className="text-2xl font-bold text-gray-900">{userInfo?.name || '未知用戶'}</h2>
              <p className="text-sm text-gray-500">{userInfo?.email || '無電子郵件'}</p>
              <div className="mt-2">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    userInfo?.role === "admin"
                      ? "bg-purple-100 text-purple-800"
                      : userInfo?.role === "maintainer"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {formatRole(userInfo?.role || 'user')}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">姓名</dt>
              <dd className="mt-1 text-sm text-gray-900">{userInfo?.name || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">電子郵件</dt>
              <dd className="mt-1 text-sm text-gray-900">{userInfo?.email || '-'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">角色</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatRole(userInfo?.role || 'user')}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">帳號狀態</dt>
              <dd className="mt-1">{formatStatus(userInfo?.isActive !== false)}</dd>
            </div>
            {userInfo?.apikey && (
              <div>
                <dt className="text-sm font-medium text-gray-500">API Key</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded">
                  {userInfo.apikey}
                </dd>
              </div>
            )}
            {userInfo?.createdAt && (
              <div>
                <dt className="text-sm font-medium text-gray-500">建立時間</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatDate(userInfo.createdAt)}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* 修改密码按钮 */}
        {(userInfo?.role === 'user' || userInfo?.role === 'maintainer') && (
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <button
              type="button"
              onClick={() => setShowChangePassword(!showChangePassword)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {showChangePassword ? '取消修改密碼' : '修改密碼'}
            </button>
          </div>
        )}
      </div>

      {/* 修改密码表单 */}
      {showChangePassword && (
        <div className="mt-6 bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg font-medium text-gray-900">修改密碼</h3>
            <p className="mt-1 text-sm text-gray-500">
              請輸入當前密碼和新密碼以修改您的帳號密碼。
            </p>
          </div>

          <form onSubmit={handleChangePassword} className="border-t border-gray-200 px-4 py-5 sm:px-6">
            <div className="space-y-6">
              {/* 当前密码 */}
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                  當前密碼 *
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                    passwordErrors.currentPassword
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  placeholder="請輸入當前密碼"
                />
                {passwordErrors.currentPassword && (
                  <p className="mt-2 text-sm text-red-600">{passwordErrors.currentPassword}</p>
                )}
              </div>

              {/* 新密码 */}
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  新密碼 *
                </label>
                <input
                  type="password"
                  id="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                    passwordErrors.newPassword
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  placeholder="請輸入新密碼"
                />
                {passwordErrors.newPassword && (
                  <p className="mt-2 text-sm text-red-600">{passwordErrors.newPassword}</p>
                )}
                <p className="mt-2 text-sm text-gray-500">
                  密碼必須至少8個字元，包含大小寫字母和數字
                </p>
              </div>

              {/* 确认密码 */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  確認新密碼 *
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                    passwordErrors.confirmPassword
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  placeholder="請再次輸入新密碼"
                />
                {passwordErrors.confirmPassword && (
                  <p className="mt-2 text-sm text-red-600">{passwordErrors.confirmPassword}</p>
                )}
              </div>
            </div>

            {/* 提交按钮 */}
            <div className="mt-6 flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowChangePassword(false);
                  setPasswordData({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                  });
                  setPasswordErrors({});
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    修改中...
                  </>
                ) : (
                  '修改密碼'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default UserProfilePage;